<template>
  <div
    v-if="show"
    class="modal-overlay"
  >
    <div class="modal-content">
      <h3>{{ t('apiKeyModal.title') }}</h3>
      <form
        v-if="!apiKeySecret"
        @submit.prevent="handleCreateApiKey"
        class="api-key-form"
      >
        <input
          v-model="newKeyLabel"
          :placeholder="t('apiKeyModal.namePlaceholder')"
        >
        <button type="submit">
          {{ t('apiKeyModal.createButton') }}
        </button>
        <p class="help-text">
          {{ t('apiKeyModal.nameHelp') }}
        </p>

        <p
          v-if="apiKeyError"
          class="error-message"
        >
          {{ apiKeyError }}
        </p>
      </form>
      <div
        v-else
        class="api-key-secret-container"
      >
        <div class="api-key-row">
          <p class="api-key-line">
            {{ t('apiKeyModal.yourKeyIs') }}
            <span class="api-key">{{ apiKeySecret }}</span>
          </p>
          <button
            class="copy-btn"
            type="button"
            @click="copyApiKey"
          >
            <span
              class="icon"
              aria-hidden="true"
            >
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  d="M16 1H6c-1.1 0-2 .9-2 2v12h2V3h10V1zm3 4H10c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h9c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H10V7h9v14z"
                  fill="currentColor"
                />
              </svg>
            </span>
            <span class="btn-text">{{ isCopied ? t('apiKeyModal.copied') : '' }}</span>
          </button>
        </div>
        <p class="success-message">
          {{ t('apiKeyModal.copyWarning') }}
        </p>
      </div>
      <div class="modal-actions">
        <button
          class="cancel-btn"
          @click="closeModal"
        >
          {{ t('apiKeyModal.close') }}
        </button>
      </div>
    </div>
    <div
      class="modal-backdrop"
      @click="closeModal"
    />
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import {r7eBaseApp} from '../services/index.js';

const { t } = useI18n();

const props = defineProps({
  show: Boolean
});
const emit = defineEmits(['close', 'created']);

const newKeyLabel = ref('');
const apiKeyError = ref('');
const apiKeySecret = ref('');
const isCopied = ref(false);

watch(() => props.show, (val) => {
  if (val) {
    newKeyLabel.value = '';
    apiKeyError.value = '';
    apiKeySecret.value = '';
  }
});

const handleCreateApiKey = async () => {
  apiKeyError.value = '';
  try {
    const newKey = await r7eBaseApp.createAccountApiKey({ name: newKeyLabel.value });
    apiKeySecret.value = newKey.secret;
    emit('created');
  } catch (e) {
    apiKeyError.value = e.message || t('apiKeyModal.error');
  }
};

const copyApiKey = async () => {
  if (!apiKeySecret.value) return;
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(apiKeySecret.value);
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = apiKeySecret.value;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    isCopied.value = true;
    setTimeout(() => {
      isCopied.value = false;
    }, 2000);
  } catch (e) {
    // Silent fail; user can manually copy
  }
};

const closeModal = () => {
  emit('close');
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}
.modal-content {
  background: var(--color-bg);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
  padding: 32px 24px 24px 24px;
  max-width: 800px;
  width: 100%;
  z-index: 2001;
  position: relative;
  text-align: center;
}
.api-key-form input {
  padding: 8px;
  margin-right: 10px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
}
.api-key-form button {
  padding: 8px 16px;
  background: var(--color-safe);
  color: var(--color-text-light);
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.api-key-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.api-key-line {
  margin: 0;
}
.api-key {
  font-family: monospace;
  background: var(--color-bg);
  padding: 2px 8px;
  border-radius: 4px;
  margin-right: 10px;
}
.copy-btn {
  padding: 8px 16px;
  background: var(--color-link);
  color: var(--color-text-light);
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.copy-btn .icon {
  display: inline-flex;
  margin-right: 6px;
  vertical-align: middle;
}
.copy-btn .btn-text {
  vertical-align: middle;
}
.error-message {
  color: var(--color-danger);
  margin-top: 10px;
}
.success-message {
  color: var(--color-safe);
  margin-top: 10px;
}
.modal-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
}
.cancel-btn {
  background-color: var(--color-border);
  color: var(--color-text);
  border: none;
  border-radius: 4px;
  padding: 8px 18px;
  font-size: 1em;
  cursor: pointer;
  transition: background-color 0.2s;
}
.cancel-btn:hover {
  background-color: var(--color-border);
  color: var(--color-text);
}
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.3);
  z-index: 2000;
}
.help-text {
  color: var(--color-description);
  font-style: italic;
  font-size: 0.9em;
  margin-top: 10px;
  margin-bottom: 10px;
}
</style> 